import { ChangeEvent, useRef, useState } from 'react';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import { Link } from 'react-router-dom';
import { CSSTransition } from 'react-transition-group';

import { IValidationResult, IValidator } from 'models/i-form-validation.interface';
import { IInputValue } from 'models/i-input-value.interface';
import { IMailerPayload } from 'models/mailer-payload.interface';
import { ISelectOption } from 'models/select/i-select-option.interface';

import { DEFAULTS, REGEX_PATTERN } from 'constants/defaults.constant';

import SectionOverlayWrapper from '../../../../components/sectionOverlayWrapper/SectionOverlayWrapper';
import Button from 'components/buttons/Button';
import Captcha from 'components/captcha/Captcha';
import Checkbox from 'components/checkbox/Checkbox';
import Form from 'components/form/Form';
import InputFormField from 'components/InputFormField/InputFormField';
import InputValidation from 'components/inputValidation/InputValidation';
import LoadingSpinner from 'components/loadingSpinner/LoadingSpinner';
import SingleSelect from 'components/singleSelect/SingleSelect';

import { useTermsOfServiceConsents } from 'hooks/useTermsOfServiceConsents';

import { ReactComponent as SuccessMarkSvg } from 'assets/contact/success_mark.svg';

interface IContactForm {
  submit: (payload: IMailerPayload, resetForm: () => void) => void;
  isSuccess: boolean;
  isLoading: boolean;
}

const ContactForm = ({ submit, isSuccess, isLoading }: IContactForm) => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [subject, setSubject] = useState<ISelectOption>({ value: '', label: '-' });
  const [company, setCompany] = useState('');
  const [message, setMessage] = useState('');
  const [captchaKey, setCaptchaKey] = useState('');
  const successMessageAnimationTimeout = 200;
  const successMessageRef = useRef(null);

  const regEmail = REGEX_PATTERN.email;
  const subjects: ISelectOption[] = [
    { value: 'public sale inquires', label: 'Public sale inquires' },
    { value: 'media', label: 'Media' },
    { value: 'marketing & Partnerships', label: 'Marketing & Partnerships' },
  ];

  const fullNameValidation = (inputValue: IInputValue): IValidationResult => {
    return (inputValue as string).length === 0 ? { valid: false, message: `Enter full name` } : { valid: true };
  };

  const emailPatternValidation = (inputValue: IInputValue): IValidationResult => {
    const value = inputValue as string;

    return regEmail.test(value) ? { valid: true } : { valid: false, message: 'Enter valid email' };
  };

  const companyNameValidation = (inputValue: IInputValue): IValidationResult => {
    return (inputValue as string).length === 0 ? { valid: false, message: `Enter company name` } : { valid: true };
  };

  const subjectValidation = (): IValidationResult => {
    return !!subject.value ? { valid: true } : { valid: false, message: 'Select subject' };
  };

  const messageValidation = (inputValue: IInputValue): IValidationResult => {
    return (inputValue as string).length === 0 ? { valid: false, message: `Enter message` } : { valid: true };
  };

  const onSubmit = () => {
    submit(
      {
        name,
        email,
        company,
        message,
        captchaKey,
        subject: subject.value,
      },
      resetForm
    );
  };

  const resetForm = () => {
    setEmail('');
    setName('');
    setSubject({ value: '', label: '-' });
    setCompany('');
    setMessage('');
  };

  const { privacyPolicyChecked, setPrivacyPolicyChecked } = useTermsOfServiceConsents();

  const requiredTrueValidator: IValidator<boolean> = (value: boolean) => {
    return value ? { valid: true } : { valid: false, message: `Consent is required` };
  };

  return (
    <div className="tw-pt-[50px] sm:tw-pt-15 xl:tw-max-w-[730px] xl:tw-pt-0">
      <h2 className="tw-mb-4 tw-text-center tw-text-[30px] tw-uppercase tw-text-white sm:tw-text-left md:tw-text-[44px] xl:tw-text-5xl">
        Drop us a message
      </h2>
      <p className="tw-text-center tw-text-lg tw-text-white sm:tw-text-left sm:tw-text-[22px]">
        Please provide a brief description of your business enquiry and we’ll do our best to get back to you as soon as
        possible. Complete the form below or drop us a line at{' '}
        <a className=" tw-text-turquoise tw-underline" href="mailto:contact@realitymeta.io">
          contact@realitymeta.io
        </a>
      </p>
      <Form onSubmit={onSubmit} className="tw-relative">
        {isLoading && (
          <SectionOverlayWrapper>
            <LoadingSpinner />
            <p className="tw-text-lg tw-uppercase tw-text-white sm:tw-text-xl">Sending your message...</p>
          </SectionOverlayWrapper>
        )}
        <CSSTransition
          unmountOnExit
          nodeRef={successMessageRef}
          in={isSuccess}
          timeout={successMessageAnimationTimeout}
        >
          <SectionOverlayWrapper className="fade-animation" inputRef={successMessageRef}>
            <SuccessMarkSvg />
            <p className="tw-text-lg tw-uppercase tw-text-white sm:tw-text-[32px]">Message sent</p>
          </SectionOverlayWrapper>
        </CSSTransition>
        <div className="tw-mt-7.5 tw-flex tw-flex-col tw-gap-5">
          <InputFormField
            label="Full name *"
            value={name}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setName(e.target.value)}
            validations={[{ key: 'fullNameValidation', validator: fullNameValidation }]}
            labelClassName="tw-uppercase tw-text-xl tw-font-semibold"
            inputClassName="tw-h-[48px] tw-w-full tw-rounded-lg tw-border-0 tw-border-solid tw-bg-inputBackground tw-p-[14px] tw-text-white focus:tw-outline-none focus-visible:tw-border focus-visible:tw-border-menuLinkColor"
          />
          <InputFormField
            label="Email *"
            value={email}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
            validations={[{ key: 'emailPatternValidation', validator: emailPatternValidation }]}
            labelClassName="tw-uppercase tw-text-xl font-semibold"
            inputClassName="tw-h-[48px] tw-w-full tw-rounded-lg tw-border-0 tw-border-solid tw-bg-inputBackground tw-p-[14px] tw-text-white focus:tw-outline-none focus-visible:tw-border focus-visible:tw-border-menuLinkColor"
          />
          <InputFormField
            label="Company name *"
            value={company}
            onChange={(e: ChangeEvent<HTMLInputElement>) => setCompany(e.target.value)}
            validations={[{ key: 'companyNameValidation', validator: companyNameValidation }]}
            labelClassName="tw-uppercase tw-text-xl font-semibold"
            inputClassName="tw-h-[48px] tw-w-full tw-rounded-lg tw-border-0 tw-border-solid tw-bg-inputBackground tw-p-[14px] tw-text-white focus:tw-outline-none focus-visible:tw-border focus-visible:tw-border-menuLinkColor"
          />
          <div>
            <SingleSelect
              label="Subject *"
              value={subject}
              options={subjects}
              onChange={(country) => setSubject(country!)}
              placeholder="Countries"
              labelClassName="tw-uppercase tw-text-xl font-semibold"
              customBackgroundColor="#0a204e"
            />
            <InputValidation value={subject.value} validator={subjectValidation} />
          </div>
          <div className="tw-flex tw-flex-col">
            <label htmlFor="message" className="font-semibold tw-mb-2.5 tw-text-xl tw-uppercase tw-text-white">
              Message *
            </label>
            <textarea
              id="message"
              value={message}
              onChange={(e: ChangeEvent<HTMLTextAreaElement>) => setMessage(e.target.value)}
              className="tw-h-[162px] tw-w-full tw-resize-none tw-rounded-lg tw-border-0 tw-bg-inputBackground tw-px-3.5 tw-py-3 tw-text-white focus:tw-outline-none"
            />
            <InputValidation value={message} validator={messageValidation} />
          </div>
        </div>
        <div className="tw-block tw-items-center tw-justify-between tw-pt-5 sm:tw-flex">
          <div className="tw-ml-0.5 tw-flex tw-items-start">
            <Checkbox
              name="contact"
              checked={privacyPolicyChecked}
              onChange={setPrivacyPolicyChecked}
              validations={[
                {
                  key: 'requiredTrueValidator',
                  validator: requiredTrueValidator,
                },
              ]}
            />
            <p className="tw-ml-2.5 tw-block tw-text-base tw-font-medium tw-text-white sm:tw-grid sm:tw-text-lg">
              <span className="tw-leading-[22px]">
                I have read the Reality NFT’s{' '}
                <Link to={DEFAULTS.privacyPolicyLink} target="_blank" className="tw-text-turquoise">
                  Privacy Policy
                </Link>
                .{' '}
              </span>
              <span className="tw-leading-[22px]">I understand it and agree with its provisions.</span>
            </p>
          </div>
          <div className="tw-flex tw-justify-center">
            <Button
              type="submit"
              className="tw-mt-5 tw-px-8 sm:tw-mt-0"
              text="Send message"
              size="medium"
              disabled={!privacyPolicyChecked}
            />
          </div>
          <GoogleReCaptchaProvider reCaptchaKey={process.env.REACT_APP_CAPTCHA_SITE_KEY || ''}>
            <Captcha onVerify={(key) => setCaptchaKey(key as string)} />
          </GoogleReCaptchaProvider>
        </div>
      </Form>
    </div>
  );
};

export default ContactForm;
